import {
    AfterContentInit,
    Component,
    ContentChild,
    ElementRef,
    EventEmitter,
    HostBinding,
    HostListener,
    Input,
    OnDestroy,
    Output,
    Renderer2,
    ViewChild, ViewEncapsulation
} from '@angular/core';
import {Colors} from '../../../../colors';
import {DropTriggerDirective} from './directives/drop-trigger.directive';
import {UiButtonComponent} from '../ui-button/ui-button.component';
import {environment} from '../../../../../../../environments/environment';


export enum DropdownColors {
    primary = 'primary',
    secondary = 'secondary',
    danger = 'danger',
    neutral = 'neutral',
}

@Component({
    selector: 'ui-dropdown',
    templateUrl: './ui-dropdown.component.html',
    styleUrls: ['./ui-dropdown.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class UiDropdownComponent implements AfterContentInit, OnDestroy {
    private _menuAppendedToBody = false;
    private _isMenuOpened = false;
    private _disabled = false;
    private _buttonContext: any;
    private _colorObserver: MutationObserver;

    @ViewChild('menu') menu: ElementRef;
    @Input() color?: keyof typeof DropdownColors = undefined;
    @Output() opened = new EventEmitter<boolean>();
    @ContentChild(DropTriggerDirective) triggerRef: DropTriggerDirective;
    @Input() menuTitle: string = undefined;
    @Input() autoClose: boolean = true;
    @Input()
    get disabled() {
        if (this.triggerRef.el.nativeElement instanceof UiButtonComponent) {
            return this._disabled || this.triggerRef.el.nativeElement.disabled;
        }

        return this._disabled;
    }
    set disabled(value: boolean) {
        this._disabled = value;
        if (this._disabled && this._menuAppendedToBody) {
            this.closeMenu();
        }

        if (this._buttonContext) {
            this._buttonContext.showActiveChevron = !this._disabled;
        }
    }

    private outsideClickUnlisten: () => void;
    private intersectionObserver: IntersectionObserver;
    private scrollParents: HTMLElement[] = [];
    private scrollListener: EventListener;

    constructor(
        private el: ElementRef,
        private renderer: Renderer2) {
    }

    get isMenuOpened() {
        return this._isMenuOpened;
    }
    set isMenuOpened(value: boolean) {
        const menuElement = this.menu.nativeElement;

        this.renderer.setStyle(menuElement, 'visibility', value ? 'visible' : 'hidden');
        this._isMenuOpened = value;
        this.opened.emit(value);

        if (this._buttonContext) {
            this._buttonContext.active = value;
        }
    }

    ngAfterContentInit(): void {
        if (!this.triggerRef && !environment.production) {
            throw new Error('UiDropdown: É necessário informar um trigger para o dropdown. Utilize a diretiva [drop-trigger] no elemento que deseja usar como trigger.');
        }

        if (this.triggerRef.button || this.triggerRef.highlight?.button) {
            this._buttonContext = this.triggerRef?.button?.context ?? this.triggerRef.highlight?.button.context;

            this._buttonContext.showActiveChevron = !this._disabled;
            this._buttonContext.active = this.isMenuOpened;
        }

        // Define a cor do dropdown
        if (!this.color) {
            if (this.triggerRef.button || this.triggerRef.highlight?.button) {
                const button = this.triggerRef.button ?? this.triggerRef.highlight?.button;

                this._colorObserver = new MutationObserver(() => {
                    this.color = button.color as DropdownColors;
                });

                this._colorObserver.observe(button.el.nativeElement, {
                    attributes: true,
                    attributeFilter: ['class', 'style']
                });

                this.color = button.color as DropdownColors;
            } else {
                const contentElement: HTMLElement = this.triggerRef.el.nativeElement;
                let childBrand: DropdownColors;

                // Procura no elemento trigger se ele possui BRANDs para deixar a cor do dropdown compatível
                if (contentElement) {
                    if (!this.color) {
                        for (const classe in contentElement.classList) {
                            if (Object.keys(Colors).includes(classe.toLowerCase())) {
                                childBrand = classe as DropdownColors;
                            }
                        }

                        if (!childBrand) {
                            for (let i = 0; i < contentElement.attributes.length; i++) {
                                const attr = contentElement.attributes.item(i);

                                if (attr.name.toLowerCase() === 'color') {
                                    if (attr.value.toUpperCase() in Colors) {
                                        childBrand = attr.value as DropdownColors;
                                    }
                                }
                            }
                        }
                    }
                }

                this.color = childBrand ?? DropdownColors.primary;
            }
        }
    }



    @HostBinding('style.border-radius')
    get getBorderRadius() {
        const el = this.triggerRef?.el?.nativeElement as HTMLElement;

        if (el) {
            return window.getComputedStyle(el).borderRadius;
        }

        return 'inherit';
    }

    @HostBinding('style.pointer-events')
    get getPointerEvents() {
        const el = this.triggerRef?.el?.nativeElement as HTMLElement;

        if (el) {
            return window.getComputedStyle(el).pointerEvents;
        }

        return 'inherit';
    }

    @HostListener('click')
    onClick() {
        if (this.disabled) {
            return;
        }

        if (this._menuAppendedToBody && this.autoClose) {
            this.closeMenu();
        } else {
            this.openMenu();
        }
    }

    openMenu() {
        // 1. append menu, visibilidade, posicionamento
        this.renderer.setStyle(this.menu.nativeElement, 'display', 'flex');
        this.renderer.appendChild(document.body, this.menu.nativeElement);
        this._menuAppendedToBody = true;
        this.setMenuPosition();
        this.isMenuOpened = true;

        // 2. listener de clique fora
        if (this.autoClose) {
            this.outsideClickUnlisten = this.renderer.listen('document', 'click', event => {
                if (!this.triggerRef.el.nativeElement.contains(event.target)) {
                    this.closeMenu();
                }
            });
        }

        // 3. observer de visibilidade
        this.intersectionObserver = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (!entry.isIntersecting) {
                    this.closeMenu();
                }
            });
        });
        this.intersectionObserver.observe(this.triggerRef.el.nativeElement);

        // 4. scroll listeners
        this.addScrollListenerToParents(this.el.nativeElement, () => {
            if (this.isMenuOpened) {
                this.setMenuPosition();
            }
        });
        this.addWindowListeners();
    }

    closeMenu() {
        // 1. remover menu DOM
        this.renderer.removeChild(document.body, this.menu.nativeElement);
        this._menuAppendedToBody = false;
        this.isMenuOpened = false;
        this.renderer.setStyle(this.menu.nativeElement, 'display', 'none');

        // 2. remover clique fora
        if (this.outsideClickUnlisten) {
            this.outsideClickUnlisten();
            this.outsideClickUnlisten = null;
        }

        // 3. disconnect observer
        if (this.intersectionObserver) {
            this.intersectionObserver.disconnect();
            this.intersectionObserver = null;
        }

        // 4. remover scroll listeners
        this.removeScrollListenersFromParents();
        this.removeWindowListeners();
    }

    ngOnDestroy(): void {
        if (this._menuAppendedToBody) {
            this.closeMenu();
        }

        if (this._colorObserver) {
            this._colorObserver.disconnect();
        }
    }

    // Função para posicionar o menu corretamente na janela
    private setMenuPosition() {
        const menuElement: HTMLElement = this.menu.nativeElement;
        const triggerRef = this.triggerRef.el.nativeElement as HTMLElement;
        const triggerRect = triggerRef.getBoundingClientRect();

        const triggerMenuOffsetWidth = Math.abs((triggerRect.width - menuElement.clientWidth));

        // posições que o menu poderá ocupar com o redimensionamento
        // Caso haja espaço abaixo
        const bottomPosition = Math.round(triggerRect.bottom + window.scrollY) + 5;
        // Caso somente haja espaço acima
        const topPosition = Math.round(triggerRect.top + window.scrollY - menuElement.clientHeight) - 5;

        // Caso haja espaço na esquerda
        const leftPosition = Math.round(triggerRect.left + window.scrollX - triggerMenuOffsetWidth/2);

        // Caso somente haja espaço a direita
        const rightPosition = Math.round(triggerRect.left + window.scrollX);
        let menuTop: number;
        let menuLeft: number;

        if (window.innerHeight > (bottomPosition - window.scrollY + menuElement.clientHeight + 5)) {
            menuTop = bottomPosition;
        } else {
            menuTop = topPosition;
        }

        if (0 < leftPosition) {
            menuLeft = leftPosition;

        } else {
            menuLeft = rightPosition;
        }

        this.renderer.setStyle(menuElement, 'left', `${menuLeft}px`);
        this.renderer.setStyle(menuElement, 'top', `${menuTop}px`);
    }

    // Adiciona um lister de scroll em todos os elementos pais
    private addScrollListenerToParents(element: HTMLElement, listener: EventListener) {
        this.scrollParents = [];
        this.scrollListener = listener;
        let current: HTMLElement | null = element;
        while (current && current !== document.body) {
            current.addEventListener('scroll', listener, {passive: true});
            this.scrollParents.push(current);
            current = current.parentElement;
        }
        document.body.addEventListener('scroll', listener, {passive: true});
        this.scrollParents.push(document.body);
    }
    // Remove os listeners de scroll adicionados posteriormente
    private removeScrollListenersFromParents() {
        if (!this.scrollListener) {
            return;
        }
        for (const el of this.scrollParents) {
            el.removeEventListener('scroll', this.scrollListener);
        }
        this.scrollParents = [];
        this.scrollListener = undefined;
    }

    // Função a ser disparada nos eventos da janela
    private windowEventListener(event: Event) {
        if (this.isMenuOpened) {
            this.setMenuPosition();
        }
    };
    // Adiciona os listener de resize e scroll na janela
    private addWindowListeners() {
        window.addEventListener('resize', this.windowEventListener, {passive: true});
        window.addEventListener('scroll', this.windowEventListener, {passive: true});
    }
    // Remove os listener de resize e scroll da janela
    private removeWindowListeners() {
        window.removeEventListener('resize', this.windowEventListener);
        window.removeEventListener('scroll', this.windowEventListener);
    }
}
